import type { ExtraditionMlaWizardDraft } from '../../../types/extraditionMlaCaseRequest';
import {
  exceedsMax,
  isTodayOrFutureIsoDate,
  maxCharLimitExceededError,
  todayIsoDateLocal,
} from '../../../utils/caseFieldValidation';
import type { RequiredErrors, RequiredFieldSpec } from '../../../utils/requiredValidation';
import { validateRequired } from '../../../utils/requiredValidation';
import { validationDateFutureOnly, validationDocReasonRequired } from '../../../utils/validationI18n';
import type { ExtraditionMlaWizardStep } from './extraditionMlaConstants';
import { draftRequiresOtherDeliveryText } from '../../../utils/deliveryMethod';
import { isHighUrgencyLevel, requiresCustodialAuthority } from './extraditionMlaHelpers';

const MAX_DESC = 2000;
const MAX_REF = 255;

export const MAX_DOC_SKIP_REASON = 255;

export function validateExtraditionMlaStep(
  step: ExtraditionMlaWizardStep,
  draft: ExtraditionMlaWizardDraft,
): { valid: boolean; errors: RequiredErrors; specs: RequiredFieldSpec[] } {
  const specs: RequiredFieldSpec[] = [];
  const extra: RequiredErrors = {};

  if (step >= 1) {
    specs.push(
      { key: 'fileCategoryId', label: 'File Category', value: draft.fileCategoryId, elementId: 'emla-fileCategory' },
      { key: 'subFileCategoryId', label: 'Sub File Category', value: draft.subFileCategoryId, elementId: 'emla-subFileCategory' },
      { key: 'description', label: 'Description', value: draft.description, elementId: 'emla-description' },
      { key: 'caseSource', label: 'Case Source', value: draft.caseSource, elementId: 'emla-caseSource' },
      { key: 'countryInvolvedId', label: 'Country Involved', value: draft.countryInvolvedId, elementId: 'emla-countryInvolved' },
      { key: 'provinceId', label: 'Province', value: draft.provinceId, elementId: 'emla-province' },
      { key: 'districtId', label: 'District', value: draft.districtId, elementId: 'emla-district' },
      { key: 'yourReferenceNumber', label: 'Your Reference Number (External)', value: draft.yourReferenceNumber, elementId: 'emla-yourRef' },
      { key: 'hasConnectedMatters', label: 'Connected matters', value: draft.hasConnectedMatters, elementId: 'emla-connected' },
    );

    if (draft.hasConnectedMatters === 'yes') {
      specs.push({
        key: 'connectedCaseNumber',
        label: 'Connected Case Number',
        value: draft.connectedCaseNumber,
        elementId: 'emla-connectedCase',
      });
      if (draft.connectedAgdFileRefAvailable) {
        specs.push({
          key: 'connectedAgdFileRef',
          label: 'Connected AGD File Reference Number',
          value: draft.connectedAgdFileRef,
          elementId: 'emla-connectedAgd',
        });
      }
    }

    if (exceedsMax(draft.description, MAX_DESC)) extra.description = maxCharLimitExceededError();
    if (exceedsMax(draft.caseSource, MAX_DESC)) extra.caseSource = maxCharLimitExceededError();
    if (exceedsMax(draft.directionOfRequest, MAX_REF)) extra.directionOfRequest = maxCharLimitExceededError();
    if (exceedsMax(draft.yourReferenceNumber, MAX_REF)) extra.yourReferenceNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.otherExternalReferences, MAX_REF)) extra.otherExternalReferences = maxCharLimitExceededError();
    if (exceedsMax(draft.caseNumber, MAX_REF)) extra.caseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedCaseNumber, MAX_REF)) extra.connectedCaseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedAgdFileRef, MAX_REF)) extra.connectedAgdFileRef = maxCharLimitExceededError();
  }

  if (step >= 2) {
    specs.push(
      { key: 'accusedName', label: 'Name of the Accused', value: draft.accusedName, elementId: 'emla-accusedName' },
      { key: 'nationalityId', label: 'Nationality', value: draft.nationalityId, elementId: 'emla-nationality' },
      {
        key: 'countryOfOriginId',
        label: 'Country of Origin',
        value: draft.countryOfOriginId,
        elementId: 'emla-countryOfOrigin',
      },
      { key: 'accusedDob', label: 'Date of Birth', value: draft.accusedDob, elementId: 'emla-accusedDob' },
      { key: 'accusedGenderId', label: 'Gender', value: draft.accusedGenderId, elementId: 'emla-accusedGender' },
      {
        key: 'custodyStatusId',
        label: 'Current Custody Status',
        value: draft.custodyStatusId,
        elementId: 'emla-custodyStatus',
      },
    );

    if (requiresCustodialAuthority(draft.custodyStatusName, draft.custodyStatusId)) {
      specs.push({
        key: 'custodialAuthority',
        label: 'Custodial Authority',
        value: draft.custodialAuthority,
        elementId: 'emla-custodialAuthority',
      });
    }

    if (exceedsMax(draft.accusedName, MAX_REF)) extra.accusedName = maxCharLimitExceededError();
    if (exceedsMax(draft.passportOrIdNumber, MAX_REF)) extra.passportOrIdNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.custodialAuthority, MAX_REF)) extra.custodialAuthority = maxCharLimitExceededError();
    if (exceedsMax(draft.lastKnownLocation, MAX_DESC)) extra.lastKnownLocation = maxCharLimitExceededError();
  }

  if (step >= 3) {
    specs.push({
      key: 'arrestWarrantAvailable',
      label: 'Arrest Warrant Available',
      value: draft.arrestWarrantAvailable,
      elementId: 'emla-arrestWarrant',
    });

    if (draft.arrestWarrantAvailable === 'yes') {
      specs.push({
        key: 'warrantReference',
        label: 'Warrant Reference',
        value: draft.warrantReference,
        elementId: 'emla-warrantReference',
      });
    }

    if (exceedsMax(draft.warrantReference, MAX_REF)) extra.warrantReference = maxCharLimitExceededError();
    if (exceedsMax(draft.applicableTreatyArrangement, MAX_REF)) extra.applicableTreatyArrangement = maxCharLimitExceededError();
  }

  if (step >= 4) {
    specs.push({
      key: 'urgencyLevelId',
      label: 'Urgency Level',
      value: draft.urgencyLevelId,
      elementId: 'emla-urgency',
    });

    if (isHighUrgencyLevel(draft.urgencyLevelName)) {
      specs.push({
        key: 'urgencyJustification',
        label: 'Urgency Justification',
        value: draft.urgencyJustification,
        elementId: 'emla-urgencyJustification',
      });
    }

    if (exceedsMax(draft.urgencyJustification, MAX_DESC)) extra.urgencyJustification = maxCharLimitExceededError();
    if (draft.deadline && !isTodayOrFutureIsoDate(draft.deadline)) {
      extra.deadline = validationDateFutureOnly();
    }
  }

  if (step >= 5) {
    specs.push({ key: 'deliveryMethodIds', label: 'Delivery Method', value: draft.deliveryMethodIds, elementId: 'emla-delivery' });
    if (draftRequiresOtherDeliveryText(draft)) {
      specs.push({
        key: 'deliveryOther',
        label: 'Other Delivery Method',
        value: draft.deliveryOther,
        elementId: 'emla-deliveryOther',
      });
    }
    if (exceedsMax(draft.deliveryOther, MAX_REF)) extra.deliveryOther = maxCharLimitExceededError();
  }

  if (step >= 6) {
    Object.assign(extra, validateStep6Documents(draft));
  }

  const reqErrs = validateRequired(specs);
  const errors = { ...reqErrs, ...extra };
  return { valid: Object.keys(errors).length === 0, errors, specs };
}

export function validateStep6Documents(draft: ExtraditionMlaWizardDraft): RequiredErrors {
  const errors: RequiredErrors = {};
  draft.documents.forEach((doc) => {
    if (doc.fileName) return;
    if (doc.mandatory && !doc.skipReason.trim()) {
      errors[`doc-reason-${doc.id}`] = validationDocReasonRequired();
      return;
    }
    if (exceedsMax(doc.skipReason, MAX_DOC_SKIP_REASON)) {
      errors[`doc-reason-${doc.id}`] = maxCharLimitExceededError();
    }
  });
  return errors;
}

export function minDeadlineDateForExtraditionMla(): string {
  return todayIsoDateLocal();
}
